import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { logIn, logOut, refreshToken, sendCode } from "@/http/auth_api/auth";
import { i18n } from "@/i18n";

export const useAuthStore = defineStore("auth", () => {
  // state
  const access_token = ref(localStorage.getItem("access_token"));
  const access_token_expire_at = ref(localStorage.getItem("access_token_expire_at"));
  // Сам refresh-токен живёт ТОЛЬКО в httpOnly-cookie и фронту недоступен; здесь
  // храним лишь момент его истечения. Ключ localStorage "refresh_token" — легаси
  // (по копипаст-багу туда писался access token), чистится в clearLocalStorageData.
  const refresh_token_expire_at = ref(localStorage.getItem("refresh_token_expire_at"));
  const user = ref(JSON.parse(localStorage.getItem("user")));
  const userApiErrorMessage = ref(null);
  const userMessage = ref(null);
  const userRequest = ref(false);

  // getters
  const accessIsExpired = computed(() => access_token_expire_at.value - Math.ceil(Date.now() / 1000) <= 0);
  const refreshIsExpired = computed(() => refresh_token_expire_at.value - Math.ceil(Date.now() / 1000) <= 0);
  const isAuthorised = computed(() => !accessIsExpired.value || !refreshIsExpired.value);
  const userName = computed(() => user.value && user.value.name);
  // Бэк отдаёт роль в user.userRole (единственное поле роли; массива roles нет).
  // Раньше читался несуществующий roles[0] — computed падал с TypeError.
  const userRole = computed(() => user.value?.userRole ?? null);
  const userDepartment = computed(() => user.value?.department);

  const refreshTimeout = ref(null);

  function initStore() {
    userRequest.value = false;
    userApiErrorMessage.value = null;
    return initRefreshTimer();
  }

  function resetErrors() {
    userRequest.value = false;
    userApiErrorMessage.value = null;
  }

  // actions
  function updateTokenData(data) {
    if (data.accessToken) {
      localStorage.setItem("access_token", data.accessToken);
      access_token.value = data.accessToken;
    }

    // expiresIn/refreshExpiresIn бэк отдаёт и в login, и в refresh (NANFin-XXX).
    // refreshExpiresIn в refresh — ОСТАТОК жизни текущего refresh-токена (ротации
    // нет), поэтому дедлайн только уточняется, а не сдвигается на новые 30 дней.
    if (data.expiresIn) {
      // in seconds
      access_token_expire_at.value = Math.ceil(Date.now() / 1000) + data.expiresIn;
    } else {
      access_token_expire_at.value = Math.ceil(Date.now() / 1000) + 3600;
    }
    localStorage.setItem("access_token_expire_at", access_token_expire_at.value);

    if (data.refreshExpiresIn) {
      refresh_token_expire_at.value = Math.ceil(Date.now() / 1000) + data.refreshExpiresIn;
    } else {
      refresh_token_expire_at.value = Math.ceil(Date.now() / 1000) + 7 * 86400; // 7d
    }
    localStorage.setItem("refresh_token_expire_at", refresh_token_expire_at.value);

    if (data.user) {
      user.value = data.user;
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("login", data.user?.email);
    }
    initRefreshTimer();
  }

  /**
   * Check access and refresh expire timers to detect time when need to get a fresh token.
   * When access token time is expired
   */
  function initRefreshTimer() {
    if (refreshTimeout.value) {
      clearTimeout(refreshTimeout.value);
      refreshTimeout.value = null;
    }
    let timer = access_token_expire_at.value - Math.ceil(Date.now() / 1000);
    if (timer <= 0) {
      let refreshDiff = refresh_token_expire_at.value - Math.ceil(Date.now() / 1000);
      if (refreshDiff > 0) {
        // refresh now
        return refreshUserToken();
      } else if (refreshDiff < 0) {
        // access data too old
        clearLocalStorageData();
      }
    }
    if (timer >= 0) {
      if (timer > 30) {
        timer -= 30;
      }
      refreshTimeout.value = setTimeout(refreshUserToken, timer * 1000);
    }
    return Promise.resolve(null);
  }

  // Single-flight (NANFin-XXX): refresh дёргают три независимых пути — таймер
  // initRefreshTimer, router guard (initStore) и 401-interceptor в http/request.js.
  // Без общего in-flight промиса они ходили в /auth/refresh параллельно, что при
  // серверной ротации убивало сессию. Ротацию на бэке убрали, но дубли запросов
  // всё равно не нужны: все конкурентные вызовы получают ОДИН общий промис.
  let refreshInFlight = null;

  function refreshUserToken() {
    if (refreshInFlight) {
      return refreshInFlight;
    }
    refreshInFlight = refreshToken(access_token.value)
      .then((res) => {
        if (res?.data && res?.status === 200) {
          updateTokenData(res?.data);
          return true;
        }
        return false;
      })
      .catch((error) => {
        if (error?.response?.status !== 401) {
          console.error("refreshUserToken error", error.response);
        }
        return false;
      })
      .finally(() => {
        refreshInFlight = null;
      });
    return refreshInFlight;
  }

  function clearLocalStorageData() {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("access_token_expire_at");
    localStorage.removeItem("refresh_token_expire_at");
    localStorage.removeItem("user");
  }

  function clearData() {
    user.value = null;
    access_token.value = null;
    access_token_expire_at.value = null;
    refresh_token_expire_at.value = null;
  }

  function userSendCode(email) {
    if (userRequest.value) {
      return Promise.resolve(false);
    }
    if (!email) {
      userApiErrorMessage.value = "Введите Email";
      return Promise.resolve(false);
    }
    userApiErrorMessage.value = null;
    userMessage.value = null;
    userRequest.value = true;

    return sendCode(email)
      .then((res) => {
        if (res?.data && res?.status === 200) {
          // Локальная разработка: бэк вернул код в ответе (DEV_EXPOSE_OTP=true, не production) —
          // показываем прямо на шаге ввода, чтобы не лезть в БД. На стендах devCode не приходит.
          if (res.data.devCode) {
            userMessage.value = "Dev-код: " + res.data.devCode;
          }
          return true;
        }
        console.error("userSendCode unexpected error", res?.data);
        return false;
      })
      .catch((error) => {
        if (error?.response?.status !== 401) {
          console.error("userSendCode error", error?.response?.data);
        }
        if (!error?.response?.data?.message) {
          userApiErrorMessage.value = i18n.t("t-auth.wrong-login-password");
        } else {
          userApiErrorMessage.value = error?.response?.data?.message;
        }
        return false;
      })
      .finally(() => {
        userRequest.value = false;
      });
  }

  function userLogin(username, code) {
    if (userRequest.value) {
      return Promise.resolve(false);
    }
    if (!username || !code) {
      userApiErrorMessage.value = "Введите код";
      return Promise.resolve(false);
    }
    userApiErrorMessage.value = null;
    userMessage.value = null;
    userRequest.value = true;

    return logIn(username, code)
      .then((res) => {
        if (res?.data && res?.status === 200) {
          updateTokenData(res?.data);
          return true;
        }
        console.error("userLogin unexpected error", res?.data);
        return false;
      })
      .catch((error) => {
        if (error?.response?.status !== 401) {
          console.error("userLogin error", error?.response?.data);
        }
        if (!error?.response?.data?.message) {
          userApiErrorMessage.value = i18n.t("t-auth.wrong-login-password");
        } else {
          userApiErrorMessage.value = error?.response?.data?.message;
        }
        return false;
      })
      .finally(() => {
        userRequest.value = false;
      });
  }

  function userLogout() {
    if (userRequest.value) {
      return Promise.resolve(false);
    }
    userRequest.value = true;

    // Локальную сессию гасим ВСЕГДА (в finally), независимо от ответа бэка.
    // Серверный логаут — best-effort (инвалидирует refresh-cookie + строку в БД),
    // но даже если он не удался, клиент не должен остаться «залогинен»: раньше на
    // ошибке localStorage не чистился и после рефреша страницы юзер возвращался.
    return logOut(access_token.value)
      .then((res) => {
        if (res?.status !== 200) {
          console.error("userLogout unexpected response", res);
        }
        return true;
      })
      .catch((error) => {
        console.error("userLogout error", error?.response?.data);
        return true;
      })
      .finally(() => {
        clearLocalStorageData();
        clearData();
        userRequest.value = false;
      });
  }

  function $reset(errorMessage = null) {
    clearData();
    clearLocalStorageData();
    if (errorMessage) {
      console.log("errorMessage", errorMessage);
      userApiErrorMessage.value = errorMessage;
    }
    userMessage.value = null;
  }

  return {
    access_token,
    access_token_expire_at,
    refresh_token_expire_at,
    userApiErrorMessage,
    userMessage,
    userRequest,

    accessIsExpired,
    refreshIsExpired,
    isAuthorised,

    user,
    userName,
    userRole,
    userDepartment,

    initStore,
    resetErrors,
    userSendCode,
    userLogin,
    userLogout,
    refreshUserToken,

    $reset,
  };
});
